// Route table and role-based access map for the HMI. Scenes are code-split
// via React.lazy(); consumers rendering `.component` must wrap it in
// <Suspense> (see AdminView / ManagerView / OperatorView).
// See docs/design-decisions.md#ui-route-code-splitting

import { lazy } from 'react';

// Import factories are kept separate from React.lazy so prefetchScenes() can
// preload chunks at idle without rendering the components.
const importDashboard   = () => import(/* webpackPrefetch: true */ '../scenes/dashboard');
const importKpi         = () => import(/* webpackPrefetch: true */ '../scenes/kpi');
const importSetup       = () => import(/* webpackPrefetch: true */ '../scenes/setup');
const importCoPilot  = () => import(/* webpackPrefetch: true */ '../scenes/coPilot');
const importStats       = () => import(/* webpackPrefetch: true */ '../scenes/stats');
const importOrders      = () => import(/* webpackPrefetch: true */ '../scenes/orders');
const importAdmin       = () => import(/* webpackPrefetch: true */ '../scenes/admin');
const importMaintenance = () => import(/* webpackPrefetch: true */ '../scenes/maintenance');
const importInsights    = () => import(/* webpackPrefetch: true */ '../scenes/insights');

const Dashboard   = lazy(importDashboard);
const KPI         = lazy(importKpi);
const Setup       = lazy(importSetup);
const CoPilot  = lazy(importCoPilot);
const Stats       = lazy(importStats);
const Orders      = lazy(importOrders);
const Admin       = lazy(importAdmin);
const Maintenance = lazy(importMaintenance);
const Insights    = lazy(importInsights);

/**
 * Kicks off background download of every scene chunk so later navigations
 * skip first-visit latency. Failures are swallowed — prefetch is best-effort
 * and React.lazy() retries on actual navigation.
 *
 * @param {string} [currentRoute] Route name whose chunk is already loading and
 *   should be skipped.
 */
export function prefetchScenes(currentRoute) {
  const factories = [
    importDashboard,
    importKpi,
    importSetup,
    importCoPilot,
    importStats,
    importOrders,
    importAdmin,
    importMaintenance,
    importInsights,
  ];
  // Skip the chunk that's already loading (the page the user is on).
  const skip = (currentRoute || '').toLowerCase();
  for (const factory of factories) {
    const tag = factory.toString();
    if (skip && tag.includes(`/scenes/${skip}`)) continue;
    factory().catch(() => { /* best-effort */ });
  }
}

// Define all available routes with their metadata
export const ROUTES = {
  DASHBOARD: {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    icon: "dashboard"
  },
  KPI: {
    path: "/kpi",
    name: "KPI",
    component: KPI,
    icon: "kpi"
  },
  SETUP: {
    path: "/setup",
    name: "Setup", 
    component: Setup,
    icon: "setup"
  },
  PLAN_ASSIST: {
    path: "/coPilot",
    name: "CoPilot",
    component: CoPilot,
    icon: "coPilot"
  },
  STATS: {
    path: "/stats",
    name: "Stats",
    component: Stats,
    icon: "stats"
  },
  ORDERS: {
    path: "/orders",
    name: "Orders",
    component: Orders,
    icon: "orders"
  },
  MAINTENANCE: {
    path: "/maintenance",
    // Sidebar label only; key + path stay "MAINTENANCE" so deep-links and
    // role ACLs keep working.
    name: "Maintenance",
    component: Maintenance,
    icon: "maintenance"
  },
  ADMIN: {
    path: "/admin",
    name: "Admin",
    component: Admin,
    icon: "admin"
  },
  INSIGHTS: {
    path: "/insights",
    // Scene has nested pages, so the <Route> must be a wildcard; sidebar
    // links and ACL checks keep using `path`.
    routePath: "/insights/*",
    name: "Insights",
    component: Insights,
    icon: "insights"
  }
};

// Define which routes each role can access
export const ROLE_ROUTES = {
  admin: [
    ROUTES.DASHBOARD,
    ROUTES.KPI,
    ROUTES.SETUP,
    ROUTES.STATS,
    ROUTES.PLAN_ASSIST,
    ROUTES.ORDERS,
    ROUTES.MAINTENANCE,
    ROUTES.ADMIN,
    ROUTES.INSIGHTS,
  ],
  manager: [
    ROUTES.DASHBOARD,
    ROUTES.KPI,
    ROUTES.STATS,
    ROUTES.PLAN_ASSIST,
  ],
  operator: [
    ROUTES.DASHBOARD,
    ROUTES.KPI,
    ROUTES.SETUP,
    ROUTES.PLAN_ASSIST,
    ROUTES.MAINTENANCE,
  ],
  customer: [
    ROUTES.ORDERS,
  ],
  analyst: [
    // Insights only — analysts view imported data via the embedded stats view
    // at /insights/programs/:id, never the production Stats page.
    ROUTES.INSIGHTS,
  ]
};

/**
 * Returns the routes a role can access.
 * @param {string} role Role name (admin, manager, operator, customer, analyst).
 * @returns {object[]} Route definitions, empty for unknown roles.
 */
export const getRoutesForRole = (role) => {
  return ROLE_ROUTES[role] || [];
};

/**
 * Checks whether a role may access a route path.
 * @param {string} role Role name.
 * @param {string} routePath Route path, e.g. "/setup".
 * @returns {boolean} True if the role's route list contains the path.
 */
export const hasRouteAccess = (role, routePath) => {
  const roleRoutes = getRoutesForRole(role);
  return roleRoutes.some(route => route.path === routePath);
};

/**
 * Returns the landing route for a role (first entry in its route list).
 * @param {string} role Role name.
 * @returns {string} Route path, falling back to "/dashboard".
 */
export const getDefaultRouteForRole = (role) => {
  const routes = getRoutesForRole(role);
  return routes.length > 0 ? routes[0].path : "/dashboard";
};
